import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import type { ProviderProductGroup, SearchFilters } from '../hooks/useProductSearch';

interface ProductSearchResultsProps {
  results: ProviderProductGroup[];
  loading: boolean;
  error: string | null;
  filters: SearchFilters;
  updateFilters: (filters: Partial<SearchFilters>) => void;
  resetFilters: () => void;
  allCategories: string[];
  allVendors: string[];
  totalItems: number;
  hasActiveFilters: boolean;
  connectedProviderIds: Set<string>;
  hideEmptyState?: boolean;
}

const ProductSearchResults: React.FC<ProductSearchResultsProps> = ({
  results,
  loading,
  error,
  filters,
  updateFilters,
  resetFilters,
  allCategories,
  allVendors,
  totalItems,
  hasActiveFilters,
  connectedProviderIds,
  hideEmptyState = false,
}) => {
  const navigate = useNavigate();
  // Track explicitly-expanded providers; everything else stays collapsed so
  // the whole list of providers is visible on screen at once.
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [localSearch, setLocalSearch] = useState(filters.searchTerm);

  // Sync local search with filters when cleared externally
  useEffect(() => {
    if (filters.searchTerm === '' && localSearch !== '') {
      setLocalSearch('');
    }
  }, [filters.searchTerm]);

  // Reset expansion when a new result set comes back
  useEffect(() => {
    setExpandedProviders(new Set());
  }, [results]);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateFilters({ searchTerm: value });
    }, 300);
  };

  const toggleProviderCollapse = (providerId: string) => {
    setExpandedProviders(prev => {
      const next = new Set(prev);
      if (next.has(providerId)) next.delete(providerId);
      else next.add(providerId);
      return next;
    });
  };

  const handleViewProvider = (providerId: string) => {
    navigate(`/providers/${providerId}/catalog`);
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return '—';
    return `€${price.toFixed(2)}`;
  };

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="glass-card rounded-2xl p-4 space-y-3">
        {/* Search input */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            ref={searchInputRef}
            type="text"
            value={localSearch}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="Search products by name, SKU, or brand across all providers..."
            className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl pl-10 pr-10 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-orange-500/40 focus:ring-1 focus:ring-orange-500/20 transition-colors"
          />
          {localSearch && (
            <button
              onClick={() => { setLocalSearch(''); updateFilters({ searchTerm: '' }); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filters.category}
            onChange={e => updateFilters({ category: e.target.value })}
            className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500/40 transition-colors appearance-none cursor-pointer min-w-[160px]"
          >
            <option value="" className="bg-[#1a1a2e]">All Categories</option>
            {allCategories.map(c => (
              <option key={c} value={c} className="bg-[#1a1a2e]">{c}</option>
            ))}
          </select>

          <select
            value={filters.vendor}
            onChange={e => updateFilters({ vendor: e.target.value })}
            className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500/40 transition-colors appearance-none cursor-pointer min-w-[160px]"
          >
            <option value="" className="bg-[#1a1a2e]">All Brands</option>
            {allVendors.map(v => (
              <option key={v} value={v} className="bg-[#1a1a2e]">{v}</option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="px-3 py-2 text-xs text-orange-400 hover:text-orange-300 transition-colors"
            >
              Clear all
            </button>
          )}

          {hasActiveFilters && !loading && (
            <span className="ml-auto text-xs text-white/30">
              {totalItems} product{totalItems !== 1 ? 's' : ''} found
            </span>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="glass-card rounded-2xl p-8 text-center">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-white/30 text-sm">Searching products...</p>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="glass-card rounded-2xl p-4 border-l-2 border-l-red-500">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Empty state - no search yet */}
      {!loading && !error && !hasActiveFilters && !hideEmptyState && (
        <div className="glass-card rounded-2xl p-8 text-center">
          <CubeIcon className="w-10 h-10 text-white/15 mx-auto mb-3" />
          <p className="text-white/40 text-sm mb-1">Search for products across all providers</p>
          <p className="text-white/25 text-xs">Type a product name, SKU, or brand, or use the filters above</p>
        </div>
      )}

      {/* Empty state - no results */}
      {!loading && !error && hasActiveFilters && results.length === 0 && (
        <div className="glass-card rounded-2xl p-8 text-center">
          <p className="text-white/40 text-sm mb-2">No products found matching your search</p>
          <button
            onClick={resetFilters}
            className="text-orange-400 text-xs hover:text-orange-300 transition-colors"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Results grouped by provider */}
      {!loading && !error && results.length > 0 && (
        <div className="space-y-2">
          <AnimatePresence>
            {results.map((group, gi) => {
              const isCollapsed = !expandedProviders.has(group.provider_id);
              const isConnected = connectedProviderIds.has(group.provider_id);

              return (
                <motion.div
                  key={group.provider_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: gi * 0.05 }}
                  className="glass-card rounded-2xl overflow-hidden"
                >
                  {/* Provider header */}
                  <div
                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/[0.03] transition-colors"
                    onClick={() => toggleProviderCollapse(group.provider_id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-white text-sm truncate">
                          {group.provider_name}
                        </h3>
                        {isConnected && (
                          <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                            Connected
                          </span>
                        )}
                      </div>
                      <span className="text-white/30 text-xs flex-shrink-0">
                        {group.products.length} product{group.products.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleViewProvider(group.provider_id); }}
                        className="px-3 py-1 text-xs rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 hover:bg-orange-500/20 transition-colors"
                      >
                        View Catalog
                      </button>
                      {isCollapsed ? (
                        <ChevronDownIcon className="w-4 h-4 text-white/30" />
                      ) : (
                        <ChevronUpIcon className="w-4 h-4 text-white/30" />
                      )}
                    </div>
                  </div>

                  {/* Product list */}
                  {!isCollapsed && (
                    <div className="border-t border-white/[0.06]">
                      {/* Table header */}
                      <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-white/25 border-b border-white/[0.04]">
                        <div className="col-span-1 text-center">Stock</div>
                        <div className="col-span-4">Product</div>
                        <div className="col-span-2">SKU</div>
                        <div className="col-span-2">Brand</div>
                        <div className="col-span-2">Category</div>
                        <div className="col-span-1 text-right">Price</div>
                      </div>

                      {group.products.map((product, pi) => (
                        <div
                          key={product.product_id}
                          className="border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.03] transition-colors px-4 py-2.5 cursor-pointer"
                          onClick={() => handleViewProvider(group.provider_id)}
                        >
                          {/* Desktop layout */}
                          <div className="hidden sm:grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-1 flex justify-center">
                              <div className={`w-2 h-2 rounded-full ${product.in_stock ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            </div>
                            <div className="col-span-4 text-white text-sm truncate">
                              {product.title}
                            </div>
                            <div className="col-span-2">
                              <span className="text-[11px] text-white/30 bg-white/[0.04] px-1.5 py-0.5 rounded font-mono">
                                {product.sku}
                              </span>
                            </div>
                            <div className="col-span-2 text-white/50 text-xs truncate">
                              {product.vendor}
                            </div>
                            <div className="col-span-2 text-white/40 text-xs">
                              {product.category || '—'}
                            </div>
                            <div className="col-span-1 text-right text-white/60 text-xs font-mono">
                              {formatPrice(product.lowest_price)}
                            </div>
                          </div>

                          {/* Mobile layout */}
                          <div className="sm:hidden space-y-1">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${product.in_stock ? 'bg-emerald-500' : 'bg-red-500'}`} />
                              <span className="text-white text-sm truncate">{product.title}</span>
                            </div>
                            <div className="flex items-center gap-3 pl-4 text-xs">
                              <span className="text-white/30 font-mono">{product.sku}</span>
                              <span className="text-white/40">{product.vendor}</span>
                              <span className="text-white/50 ml-auto font-mono">{formatPrice(product.lowest_price)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default ProductSearchResults;
